import { useQuery } from "@apollo/client";
import { createContext, useContext, useEffect, useState } from "react";
import { loadUiComponent } from "../graphql/queries/loadUiComponent";

/**
 * @typedef {Object} Campaigns
 * @property {Object} campaignListConfig - Configuration for the campaign list
 * @property {Object} campaignSummary - Configuration for the campaign summary
 * @property {Object} cardConfigurations - Configuration for card settings
 */

/**
 * @typedef {Object} AppConfig
 * @property {Object} header - Header configuration
 * @property {Object} navigation - Navigation menu configuration
 * @property {Object} permissions - Permission settings
 * @property {Object} featureFlags - Feature flags for the application
 * @property {string} programName - Name of the program
 * @property {Campaigns} campaigns - Campaigns configuration
 */

/**
 * Context for app configuration
 * @type {React.Context<{
 *   appConfig: AppConfig | null,
 *   loading: boolean,
 *   programName: string | null,
 *   featureFlags: Object | null,
 *   error: Error | null
 * }>}
 */
export const AppConfigContext = createContext(null);

/**
 * Provider component for app configuration
 * @param {Object} props - Component props
 * @param {string} props.programId - Program ID
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} AppConfigProvider component
 */
export function AppConfigProvider({ programId, children }) {
  // Use programId prop if provided, otherwise try to get from localStorage
  const [appConfig, setAppConfig] = useState(null);

  // Use the loadUiComponent query to fetch the configuration
  const { loading, error, data } = useQuery(loadUiComponent, {
    variables: { slug: "app-config" },
    skip: !programId,
  });

  useEffect(() => {
    if (data?.loadUiComponent?.uiSchema) {
      try {
        const config = data?.loadUiComponent?.uiSchema;
        localStorage.setItem("programName", config?.programName);
        // Parse the UI schema which should contain our configuration
        setAppConfig(config);
      } catch (err) {
        console.error("Error parsing app configuration:", err);
      }
    }
  }, [data]);

  const contextValue = {
    appConfig,
    programName: appConfig?.programName,
    featureFlags: appConfig?.featureFlags,
    loading,
    error,
  };

  return (
    <AppConfigContext.Provider value={contextValue}>
      {children}
    </AppConfigContext.Provider>
  );
}

/**
 * Hook to use app configuration
 * @returns {{
 *   appConfig: AppConfig | null,
 *   loading: boolean,
 *   programName: string | null,
 *   featureFlags: Object | null,
 *   error: Error | null
 * }} App configuration state
 */
export function useAppConfig() {
  const context = useContext(AppConfigContext);
  if (context === null) {
    throw new Error("useAppConfig must be used within a AppConfigProvider");
  }
  return context;
}

export default AppConfigProvider;
